import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { AppPricingInterval, CurrentSubscription, Plan } from "@/api/account/type.d"

import {
  DowngradeConditions,
  getDowngradeConditions,
  getFeatureList,
  getImportantNoticeText,
  getModalConfig,
  TempPlanInfo,
} from "./utils"

export interface UseDowngradeModalParams {
  currentSubscription: CurrentSubscription
  tempPlanInfo: TempPlanInfo
  currentPlan?: Plan
}

export interface UseDowngradeModalReturn {
  conditions: DowngradeConditions
  modalConfig: {
    title: string
    primaryActionContent: string
    secondaryActionContent: string
  }
  featureList: string[]
  noticeText: string
  // 判断函数
  isDowngradeScenario: () => boolean
  isUpgradeScenario: () => boolean
  isPlanChangeScenario: () => boolean
  // 文案获取函数
  getMainDescription: (customParams?: Record<string, any>) => string
  getWarningMessage: () => string
}

/**
 * 降级Modal的逻辑hook，可在bundle套餐等其他场景复用
 * @param params 参数对象
 * @returns 返回处理好的数据和方法
 */
export const useDowngradeModal = (params: UseDowngradeModalParams): UseDowngradeModalReturn => {
  const { t } = useTranslation()
  const { currentSubscription, tempPlanInfo, currentPlan } = params

  // 计算条件判断
  const conditions = useMemo(
    () => getDowngradeConditions(tempPlanInfo, currentSubscription),
    [tempPlanInfo, currentSubscription],
  )

  // 计算Modal配置
  const modalConfig = useMemo(
    () => getModalConfig(conditions, t),
    [conditions, t],
  )

  // 计算功能列表
  const featureList = useMemo(
    () => getFeatureList(currentPlan, t),
    [currentPlan, t],
  )

  // 计算重要提示文案
  const noticeText = useMemo(
    () => getImportantNoticeText(conditions, currentSubscription, t),
    [conditions, currentSubscription, t],
  )

  // 判断是否是降级场景
  const isDowngradeScenario = useCallback(() => {
    return conditions.isDowngrade
  }, [conditions.isDowngrade])

  // 判断是否是升级场景
  const isUpgradeScenario = useCallback(() => {
    return tempPlanInfo ? tempPlanInfo.level > currentSubscription.level : false
  }, [tempPlanInfo, currentSubscription.level])

  // 判断是否是套餐变更场景
  const isPlanChangeScenario = useCallback(() => {
    return conditions.isLowYearToHighMonth || conditions.isHighMonthToLowerYear
  }, [conditions.isLowYearToHighMonth, conditions.isHighMonthToLowerYear])

  // 获取主要描述文案
  const getMainDescription = useCallback((customParams?: Record<string, any>) => {
    if (conditions.isAPPToBundle && conditions.isDowngrade) {
      return t("account:DowngradeReminderDesc4", {
        current_plan_name: currentSubscription?.plan_name,
        next_plan_name: tempPlanInfo?.planName,
        current_interval: currentSubscription?.interval === AppPricingInterval.Every30_Days ? t("common:Monthly") : t("common:Annually"),
        next_interval: tempPlanInfo?.currentPlanInterval === AppPricingInterval.Every30_Days ? t("common:Monthly") : t("common:Annually"),

        ...customParams,
      })
    }

    if (conditions.isHighMonthToLowerYear) {
      return t("account:DowngradeReminderDesc3", {
        current_plan_name: currentSubscription?.plan_name,
        next_plan_name: tempPlanInfo?.planName,
        ...customParams,
      })
    }

    return t("account:DowngradeReminderDesc", {
      date: currentSubscription.billing_cycle_end_date,
      ...customParams,
    })
  }, [conditions.isAPPToBundle, conditions.isDowngrade, conditions.isHighMonthToLowerYear, t, currentSubscription.billing_cycle_end_date, currentSubscription?.plan_name, currentSubscription?.interval, tempPlanInfo?.planName, tempPlanInfo?.currentPlanInterval])

  // 获取警告信息
  const getWarningMessage = useCallback(() => {
    if (conditions.isLowYearToHighMonth) {
      return t("account:LowYearToHighMonth")
    }

    if (conditions.isDowngrade) {
      return t("account:DowngradeReminderImportant", {
        planName: currentSubscription.plan_name,
      })
    }

    return ""
  }, [conditions, currentSubscription, t])

  return {
    conditions,
    modalConfig,
    featureList,
    noticeText,
    isDowngradeScenario,
    isUpgradeScenario,
    isPlanChangeScenario,
    getMainDescription,
    getWarningMessage,
  }
}
